import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import duration from 'dayjs/plugin/duration'
import {
  LBSheet,
  LBSheetContent,
  LBSheetHeader,
  LBSheetTitle,
  LBScrollArea,
  LBLoader,
  LBAlert,
  LBAlertDescription
} from '@linearb/buzz'
import { AiProviderLabel } from '../../custom-components/SensorsIcons'
import BackfillDeltaTiles from './BackfillDeltaTiles'
import { useBackfillResult } from '../../hooks/queries/useOrganizations'
import { useConfirmBackfillResult } from '../../hooks/useConfirmBackfillResult'
import { parseUtc, inclusiveDays } from '../../utils/dateHelpers'
import type { BackfillJob } from '../../hooks/useAiBackfill'
import { matchBackfillPreset } from './AiBackfillDialog'
import { PresetLabel } from './AiBackfillHistorySheet'

dayjs.extend(relativeTime)
dayjs.extend(duration)

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  job: BackfillJob | null
}

const AiBackfillResultDrawer: React.FC<Props> = ({
  open,
  onOpenChange,
  job
}) => {
  const { data, isLoading, isError } = useBackfillResult(
    job?.orgId ?? '',
    job?.saId ?? null,
    job?.id ?? null,
    open && !!job
  )
  const { after, outcome } = useConfirmBackfillResult(data, open)

  return (
    <LBSheet open={open} onOpenChange={onOpenChange}>
      <LBSheetContent
        side='right'
        className='w-full sm:max-w-xl p-0 flex flex-col'
      >
        <LBSheetHeader className='px-6 pt-6 pb-4'>
          <LBSheetTitle className='text-base flex flex-wrap items-center gap-2'>
            <AiProviderLabel tool={job?.tool} method={job?.method} size={20} />
            <span className='text-muted-foreground font-normal'>
              · backfill result
            </span>
          </LBSheetTitle>
          {job && (
            <div className='flex flex-col gap-0.5'>
              <span className='text-sm text-foreground'>
                {job.orgName ?? `Org ${job.orgId}`}{' '}
                <span className='text-xs text-muted-foreground'>
                  (Org {job.orgId})
                </span>
              </span>
              <span className='text-xs text-muted-foreground'>
                <PresetLabel
                  label={matchBackfillPreset(
                    job.since,
                    job.until,
                    dayjs(job.startedAt)
                  )}
                />
                {job.since} → {job.until} ·{' '}
                {inclusiveDays(job.since, job.until)} days
                {job.connectedAt
                  ? ` · token connected ${parseUtc(job.connectedAt).fromNow()}`
                  : ''}
                {job.durationSeconds != null
                  ? ` · took ${dayjs
                      .duration(job.durationSeconds * 1000)
                      .format('m:ss')}`
                  : ''}
              </span>
            </div>
          )}
        </LBSheetHeader>

        <LBScrollArea className='flex-1 px-6 pb-6'>
          <div className='flex flex-col gap-4'>
            {isLoading && (
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <LBLoader className='h-4 w-4' /> Loading result…
              </div>
            )}

            {isError && (
              <LBAlert variant='attention'>
                <LBAlertDescription>
                  Couldn&apos;t load the stored result (it may have expired
                  after 90 days).
                </LBAlertDescription>
              </LBAlert>
            )}

            {data && (
              <BackfillDeltaTiles
                before={data.before}
                after={after}
                outcome={outcome}
                tool={data.tool ?? job?.tool}
              />
            )}
          </div>
        </LBScrollArea>
      </LBSheetContent>
    </LBSheet>
  )
}

export default AiBackfillResultDrawer
